/**
 * Platform-admin sample data: the client companies onboarded onto the
 * platform, the admin members who run it, and the tenant databases behind
 * them. Used only by the `module: 'admin'` tours.
 */

import type { DatabaseItem } from '@/services/admin/database-service';
import type { RegionItem } from '@/services/types';

export interface TourClient {
  id: number;
  clientId: number;
  companyName: string;
  companyShortName: string;
  country: string;
  region: string;
  status: string;
  statusName: string;
  customDomain: string;
  customDomainStatus: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  tenantId: number;
  databaseName: string;
  createdAt: string;
  createdBy: string;
  onboardedOn: string;
  timezone: string;
  currency: string;
  logoUrl: string | null;
}

export const TOUR_CLIENTS: readonly TourClient[] = Object.freeze([
  {
    id: 2101,
    clientId: 2101,
    companyName: 'Northwind Holdings Pte Ltd',
    companyShortName: 'Northwind',
    country: 'Singapore',
    region: 'APAC',
    status: 'Active',
    statusName: 'Active',
    customDomain: 'northwind.demo.sharesvision.example',
    customDomainStatus: 'Verified',
    contactName: 'Sarah Lim',
    contactEmail: 'sarah.lim@demo.sharesvision.example',
    contactNumber: '+65 6555 0101',
    tenantId: 501,
    databaseName: 'sv_tour_northwind',
    createdAt: '2024-01-10T09:00:00Z',
    createdBy: 'Platform Admin',
    onboardedOn: '2024-01-12T10:30:00Z',
    timezone: 'Asia/Singapore',
    currency: 'SGD',
    logoUrl: null,
  },
  {
    id: 2102,
    clientId: 2102,
    companyName: 'Meridian Industries Berhad',
    companyShortName: 'Meridian',
    country: 'Malaysia',
    region: 'APAC',
    status: 'Active',
    statusName: 'Active',
    customDomain: 'meridian.demo.sharesvision.example',
    customDomainStatus: 'Verified',
    contactName: 'Nurul Hassan',
    contactEmail: 'nurul.hassan@demo.sharesvision.example',
    contactNumber: '+60 3 5555 0202',
    tenantId: 502,
    databaseName: 'sv_tour_meridian',
    createdAt: '2024-05-22T11:15:00Z',
    createdBy: 'Platform Admin',
    onboardedOn: '2024-05-24T08:45:00Z',
    timezone: 'Asia/Kuala_Lumpur',
    currency: 'MYR',
    logoUrl: null,
  },
  {
    id: 2103,
    clientId: 2103,
    companyName: 'Harbourline Logistics Ltd',
    companyShortName: 'Harbourline',
    country: 'Hong Kong',
    region: 'APAC',
    status: 'Pending',
    statusName: 'Pending',
    customDomain: 'harbourline.demo.sharesvision.example',
    customDomainStatus: 'Pending Verification',
    contactName: 'Jason Ho',
    contactEmail: 'jason.ho@demo.sharesvision.example',
    contactNumber: '+852 5555 0303',
    tenantId: 503,
    databaseName: 'sv_tour_harbourline',
    createdAt: '2025-08-02T14:20:00Z',
    createdBy: 'Platform Admin',
    onboardedOn: '2025-08-02T14:20:00Z',
    timezone: 'Asia/Hong_Kong',
    currency: 'HKD',
    logoUrl: null,
  },
  {
    id: 2104,
    clientId: 2104,
    companyName: 'Crestview Energy Inc',
    companyShortName: 'Crestview',
    country: 'India',
    region: 'APAC',
    status: 'Inactive',
    statusName: 'Inactive',
    customDomain: 'crestview.demo.sharesvision.example',
    customDomainStatus: 'Not Configured',
    contactName: 'Divya Nair',
    contactEmail: 'divya.nair@demo.sharesvision.example',
    contactNumber: '+91 22 5555 0404',
    tenantId: 504,
    databaseName: 'sv_tour_crestview',
    createdAt: '2023-11-14T10:05:00Z',
    createdBy: 'Platform Admin',
    onboardedOn: '2023-11-16T09:30:00Z',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    logoUrl: null,
  },
]);

export const TOUR_PRIMARY_CLIENT = TOUR_CLIENTS[0];

export interface TourAdminMember {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  contactNumber: string;
  role: string;
  status: string;
  statusName: string;
  createdAt: string;
  lastLogin: string;
}

export const TOUR_ADMIN_MEMBERS: readonly TourAdminMember[] = Object.freeze([
  {
    id: 3101,
    userId: 3101,
    firstName: 'Elena',
    lastName: 'Duarte',
    fullName: 'Elena Duarte',
    email: 'elena.duarte@demo.sharesvision.example',
    contactNumber: '+65 6555 0900',
    role: 'Platform Administrator',
    status: 'Active',
    statusName: 'Active',
    createdAt: '2023-09-04T09:00:00Z',
    lastLogin: '2025-09-22T07:40:00Z',
  },
  {
    id: 3102,
    userId: 3102,
    firstName: 'Marcus',
    lastName: 'Feld',
    fullName: 'Marcus Feld',
    email: 'marcus.feld@demo.sharesvision.example',
    contactNumber: '+65 6555 0901',
    role: 'Support Engineer',
    status: 'Active',
    statusName: 'Active',
    createdAt: '2024-02-19T09:00:00Z',
    lastLogin: '2025-09-21T16:12:00Z',
  },
  {
    id: 3103,
    userId: 3103,
    firstName: 'Ingrid',
    lastName: 'Sorensen',
    fullName: 'Ingrid Sorensen',
    email: 'ingrid.sorensen@demo.sharesvision.example',
    contactNumber: '+65 6555 0902',
    role: 'Onboarding Specialist',
    status: 'Inactive',
    statusName: 'Inactive',
    createdAt: '2024-06-11T09:00:00Z',
    lastLogin: '2025-03-30T10:02:00Z',
  },
]);

export const TOUR_DATABASES: readonly DatabaseItem[] = Object.freeze(
  TOUR_CLIENTS.map(client => ({
    tenantId: client.tenantId,
    tenantName: client.companyShortName,
    companyName: client.companyName,
    companyShortName: client.companyShortName,
    databaseName: client.databaseName,
    activeStatus: client.status === 'Active' ? 'Active' : 'Inactive',
  }))
);

export const TOUR_REGIONS: readonly RegionItem[] = Object.freeze([
  { id: 1, regionCode: 'APAC', regionName: 'Asia Pacific' },
  { id: 2, regionCode: 'EMEA', regionName: 'Europe, Middle East & Africa' },
  { id: 3, regionCode: 'AMER', regionName: 'Americas' },
]);

/** DNS records shown by the client-onboarding domain verification step. */
export const TOUR_DNS_RECORDS = Object.freeze([
  {
    type: 'CNAME',
    host: 'northwind',
    value: 'tenants.demo.sharesvision.example',
    status: 'Verified',
    ttl: 3600,
  },
  {
    type: 'TXT',
    host: '_sharesvision.northwind',
    value: 'sv-domain-verification=tour-sample-token',
    status: 'Verified',
    ttl: 3600,
  },
]);
